import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, TrendingDown, DollarSign, PieChart, BarChart3, 
  FileText, Download, Building2, Users, Target, ArrowUpRight,
  ArrowDownRight, Briefcase, Calculator, ChevronRight
} from 'lucide-react';
import { 
  BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, Area, AreaChart
} from 'recharts';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4', '#EC4899'];

const getDemoCloudPLData = () => ({
  period: 'March 2026',
  income_statement: {
    total_revenue: 2450000,
    total_cloud_costs: 284500,
    gross_margin: 2165500,
    gross_margin_percent: 88.4,
    cost_of_revenue_percent: 11.6,
    yoy_change: -8.2,
  },
  cost_breakdown: [
    { category: 'Compute (EC2/VMs)', amount: 118500, percent: 41.7, change: -5.2 },
    { category: 'Storage (S3/Blob)', amount: 42300, percent: 14.9, change: 2.1 },
    { category: 'Database (RDS/SQL)', amount: 38700, percent: 13.6, change: -1.8 },
    { category: 'Network & CDN', amount: 28400, percent: 10.0, change: 8.4 },
    { category: 'Serverless (Lambda)', amount: 18400, percent: 6.5, change: 12.3 },
    { category: 'AI/ML Services', amount: 22800, percent: 8.0, change: 34.5 },
    { category: 'Other', amount: 15400, percent: 5.4, change: -2.1 },
  ],
  trend: [
    { month: 'Oct', revenue: 2280000, cloud_cost: 312000 },
    { month: 'Nov', revenue: 2320000, cloud_cost: 305000 },
    { month: 'Dec', revenue: 2380000, cloud_cost: 298000 },
    { month: 'Jan', revenue: 2400000, cloud_cost: 292000 },
    { month: 'Feb', revenue: 2420000, cloud_cost: 288000 },
    { month: 'Mar', revenue: 2450000, cloud_cost: 284500 },
  ],
  unit_economics: {
    cost_per_customer: 14.23,
    cost_per_transaction: 0.0047,
    cost_per_api_call: 0.00012,
  }
});

const CloudPL = () => {
  const { shouldShowData } = useDemoData();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('this-month');
  const [plData, setPLData] = useState(null);
  const [boardDeckData, setBoardDeckData] = useState(null);
  const [activeTab, setActiveTab] = useState('income');

  useEffect(() => {
    fetchPLData();
  }, [period]);

  const fetchPLData = async () => {
    setLoading(true);
    try {
      const [plRes, boardRes] = await Promise.all([
        axios.get(`${API_URL}/executive/cloud-pl?period=${period}`),
        axios.get(`${API_URL}/executive/board-deck?period=${period}`)
      ]);
      setPLData(plRes.data);
      setBoardDeckData(boardRes.data);
      
      // If data is all zeros and demo user, use demo data
      if (plRes.data.income_statement?.total_revenue === 0 && plRes.data.income_statement?.total_cloud_costs === 0 && shouldShowData) {
        setPLData(getDemoCloudPLData());
      }
    } catch (error) {
      console.error('Failed to fetch Cloud P&L data:', error);
      if (shouldShowData) {
        setPLData(getDemoCloudPLData());
      } else {
        toast.error('Failed to load financial data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  const downloadBoardDeck = async () => {
    toast.success('Generating Board Deck PDF...');
    try {
      const response = await axios.get(`${API_URL}/reports/board-deck-pdf?period=${period}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CARTIEAI_Board_Deck_${plData?.period?.replace(/ /g, '_') || 'Report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Board Deck downloaded!');
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="cloud-pl-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-20" /></CardContent></Card>
          ))}
        </div>
        <Card><CardContent className="p-6"><Skeleton className="h-80" /></CardContent></Card>
      </div>
    );
  }

  const incomeStatement = plData?.income_statement || {};
  const productMargins = plData?.product_margins || {};
  const unitEconomics = plData?.unit_economics || {};
  const kpis = plData?.kpis || {};
  const balanceSheet = plData?.balance_sheet || {};
  const trends = boardDeckData?.trends || [];

  // Prepare chart data
  const costBreakdownData = Object.entries(incomeStatement.cost_breakdown_by_category || {}).map(([name, value]) => ({
    name,
    value: Math.round(value)
  }));

  const providerData = Object.entries(incomeStatement.cost_breakdown_by_provider || {}).map(([name, value]) => ({
    name,
    value: Math.round(value)
  }));

  const productData = Object.entries(productMargins).map(([product, data]) => ({
    name: product,
    revenue: data.revenue,
    cost: data.attributed_cost,
    margin: data.gross_margin,
    marginPercent: data.margin_percent
  }));

  return (
    <div className="p-6 space-y-6" data-testid="cloud-pl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Building2 className="w-7 h-7 text-emerald-600" />
            Cloud P&L Statement
          </h1>
          <p className="text-slate-400 dark:text-slate-400 text-sm mt-1">
            CFO-grade financial reporting for cloud infrastructure
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-40" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">Year to Date</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={downloadBoardDeck} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="download-deck-btn">
            <Download className="w-4 h-4" />
            Board Deck
          </Button>
        </div>
      </div>

      {/* Period Badge */}
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 px-3 py-1">
          <FileText className="w-3 h-3 mr-1" />
          {plData?.period}
        </Badge>
      </div>

      {/* Executive Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 border-emerald-200 dark:border-emerald-800" data-testid="revenue-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Total Revenue</p>
                <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100 mt-1">
                  {formatCurrency(incomeStatement.total_revenue || 0)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-200/50 dark:bg-emerald-800/50">
                <TrendingUp className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
              </div>
            </div>
            <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-2">Allocated from product lines</p>
          </CardContent>
        </Card>

        <Card className="bg-rose-50 bg-gradient-to-br from-rose-50 to-rose-100/50 dark:from-rose-900/20 dark:to-rose-800/10 border-rose-200 dark:border-rose-800" data-testid="cost-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-rose-700 dark:text-rose-400">Cloud Costs</p>
                <p className="text-2xl font-bold text-rose-900 dark:text-rose-100 mt-1">
                  {formatCurrency(incomeStatement.total_cloud_costs || 0)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-rose-200/50 dark:bg-rose-800/50">
                <DollarSign className="w-5 h-5 text-rose-700 dark:text-rose-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-xs text-rose-600 dark:text-rose-500">
              <span>{kpis.cloud_cost_ratio}% of revenue</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-sky-50 bg-gradient-to-br from-sky-50 to-sky-100/50 dark:from-sky-900/20 dark:to-sky-800/10 border-emerald-200 dark:border-sky-800" data-testid="margin-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-700 dark:text-sky-400">Gross Margin</p>
                <p className="text-2xl font-bold text-sky-900 dark:text-sky-100 mt-1">
                  {formatCurrency(incomeStatement.gross_margin || 0)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-sky-200/50 dark:bg-sky-800/50">
                <Target className="w-5 h-5 text-emerald-700 dark:text-sky-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-xs">
              <Badge className={`${incomeStatement.gross_margin_percent >= 60 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                {incomeStatement.gross_margin_percent}% margin
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 bg-gradient-to-br from-emerald-50 to-cyan-100/50 dark:from-emerald-900/20 dark:to-cyan-800/10 border-emerald-200 dark:border-cyan-800" data-testid="forecast-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-700 dark:text-cyan-400">Next Month Forecast</p>
                <p className="text-2xl font-bold text-cyan-900 dark:text-cyan-100 mt-1">
                  {formatCurrency(kpis.forecast_next_month || 0)}
                </p>
              </div>
              <div className="p-2 rounded-lg bg-cyan-200/50 dark:bg-cyan-800/50">
                <Calculator className="w-5 h-5 text-emerald-700 dark:text-cyan-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 dark:text-emerald-500">
              {kpis.month_over_month_change >= 0 ? (
                <><ArrowUpRight className="w-3 h-3" /> +{kpis.month_over_month_change}% vs current</>
              ) : (
                <><ArrowDownRight className="w-3 h-3" /> {kpis.month_over_month_change}% vs current</>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Different Views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 dark:bg-slate-800">
          <TabsTrigger value="income" className="gap-2" data-testid="income-tab">
            <FileText className="w-4 h-4" />
            Income Statement
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-2" data-testid="products-tab">
            <Briefcase className="w-4 h-4" />
            Product Margins
          </TabsTrigger>
          <TabsTrigger value="unit" className="gap-2" data-testid="unit-tab">
            <Users className="w-4 h-4" />
            Unit Economics
          </TabsTrigger>
          <TabsTrigger value="balance" className="gap-2" data-testid="balance-tab">
            <BarChart3 className="w-4 h-4" />
            Balance Sheet
          </TabsTrigger>
        </TabsList>

        {/* Income Statement Tab */}
        <TabsContent value="income" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Breakdown by Category */}
            <Card data-testid="cost-breakdown-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Cost Breakdown by Category</CardTitle>
                <CardDescription>Cloud infrastructure spending distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <RechartsPie>
                      <Pie
                        data={costBreakdownData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {costBreakdownData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Cost by Provider */}
            <Card data-testid="provider-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Cost by Cloud Provider</CardTitle>
                <CardDescription>Multi-cloud spending comparison</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                    <BarChart data={providerData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" fill="#059669" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trends Chart */}
          <Card data-testid="trends-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Revenue vs Cloud Cost Trends</CardTitle>
              <CardDescription>6-month historical comparison</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                  <AreaChart data={trends}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorCostPL" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#E11D48" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#E11D48" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke="#059669" fill="url(#colorRevenue)" name="Revenue" />
                    <Area type="monotone" dataKey="cloud_cost" stroke="#E11D48" fill="url(#colorCostPL)" name="Cloud Cost" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Product Margins Tab */}
        <TabsContent value="products" className="space-y-6">
          <Card data-testid="product-margins">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Product Line Profitability</CardTitle>
              <CardDescription>Gross margin analysis by product</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80 mb-6">
                <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                  <BarChart data={productData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-20} textAnchor="end" height={80} />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="revenue" fill="#059669" name="Revenue" />
                    <Bar dataKey="cost" fill="#E11D48" name="Cloud Cost" />
                    <Bar dataKey="margin" fill="#0EA5E9" name="Gross Margin" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Product Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-4 font-semibold text-slate-400 dark:text-slate-400">Product Line</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-400 dark:text-slate-400">Revenue</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-400 dark:text-slate-400">Cloud Cost</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-400 dark:text-slate-400">Gross Margin</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-400 dark:text-slate-400">Margin %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(productMargins).map(([product, data], idx) => (
                      <tr key={idx} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-3 px-4 font-medium text-slate-900 dark:text-white">{product}</td>
                        <td className="py-3 px-4 text-right text-emerald-600 dark:text-emerald-400">{formatCurrency(data.revenue)}</td>
                        <td className="py-3 px-4 text-right text-rose-600 dark:text-rose-400">{formatCurrency(data.attributed_cost)}</td>
                        <td className="py-3 px-4 text-right text-emerald-600 dark:text-sky-400 font-semibold">{formatCurrency(data.gross_margin)}</td>
                        <td className="py-3 px-4 text-right">
                          <Badge className={`${data.margin_percent >= 70 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : data.margin_percent >= 50 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400'}`}>
                            {data.margin_percent}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Unit Economics Tab */}
        <TabsContent value="unit" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card data-testid="revenue-per-customer">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">Revenue per Customer</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                  {formatCurrency(unitEconomics.revenue_per_customer || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Monthly average</p>
              </CardContent>
            </Card>

            <Card data-testid="cost-per-customer">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">Cost per Customer</p>
                <p className="text-3xl font-bold text-rose-600 dark:text-rose-400 mt-2">
                  {formatCurrency(unitEconomics.cost_per_customer || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Cloud infrastructure only</p>
              </CardContent>
            </Card>

            <Card data-testid="margin-per-customer">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">Margin per Customer</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-2">
                  {formatCurrency(unitEconomics.margin_per_customer || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Gross profit per customer</p>
              </CardContent>
            </Card>

            <Card data-testid="ltv-estimate">
              <CardContent className="p-6">
                <p className="text-sm font-medium text-slate-400 dark:text-slate-400">36-Month LTV</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-sky-400 mt-2">
                  {formatCurrency(unitEconomics.ltv_estimate || 0)}
                </p>
                <p className="text-xs text-slate-400 mt-2">Estimated lifetime value</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Unit Economics Breakdown</CardTitle>
              <CardDescription>Per-customer cloud cost analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-3">LTV/CAC Ratio</h4>
                  <div className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">
                    {unitEconomics.cac_ratio?.toFixed(1) || '0'}x
                  </div>
                  <p className="text-sm text-slate-400 mt-2">
                    {unitEconomics.cac_ratio >= 3 ? 'Excellent - sustainable growth' : unitEconomics.cac_ratio >= 2 ? 'Good - room for improvement' : 'Needs attention'}
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Payback Period</h4>
                  <div className="text-4xl font-bold text-emerald-600 dark:text-sky-400">
                    {Math.round(12 / (unitEconomics.cac_ratio || 1))} mo
                  </div>
                  <p className="text-sm text-slate-400 mt-2">Time to recover acquisition cost</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-3">Cloud Cost %</h4>
                  <div className="text-4xl font-bold text-rose-600 dark:text-rose-400">
                    {kpis.cost_per_dollar_revenue ? (kpis.cost_per_dollar_revenue * 100).toFixed(1) : '0'}%
                  </div>
                  <p className="text-sm text-slate-400 mt-2">Of every dollar revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Sheet Tab */}
        <TabsContent value="balance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card data-testid="cloud-assets">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-emerald-700 dark:text-emerald-400">Cloud Assets</CardTitle>
                <CardDescription>Prepaid commitments and reserved capacity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Reserved Instances Value</p>
                      <p className="text-sm text-slate-400">Committed compute capacity</p>
                    </div>
                    <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(balanceSheet?.cloud_assets?.reserved_instances_value || 0)}
                    </p>
                  </div>
                  <div className="flex justify-between items-center p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Prepaid Commitments</p>
                      <p className="text-sm text-slate-400">Savings plans and prepayments</p>
                    </div>
                    <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(balanceSheet?.cloud_assets?.prepaid_commitments || 0)}
                    </p>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4 flex justify-between items-center">
                    <p className="font-semibold text-slate-900 dark:text-white">Total Assets</p>
                    <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency((balanceSheet?.cloud_assets?.reserved_instances_value || 0) + (balanceSheet?.cloud_assets?.prepaid_commitments || 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="cloud-liabilities">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-rose-700 dark:text-rose-400">Cloud Liabilities</CardTitle>
                <CardDescription>Future commitments and pending invoices</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 rounded-lg bg-rose-50 dark:bg-rose-900/20">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Committed Spend</p>
                      <p className="text-sm text-slate-400">Future cloud commitments</p>
                    </div>
                    <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                      {formatCurrency(balanceSheet?.cloud_liabilities?.committed_spend || 0)}
                    </p>
                  </div>
                  <div className="flex justify-between items-center p-4 rounded-lg bg-rose-50 dark:bg-rose-900/20">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">Pending Invoices</p>
                      <p className="text-sm text-slate-400">Unbilled usage</p>
                    </div>
                    <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                      {formatCurrency(balanceSheet?.cloud_liabilities?.pending_invoices || 0)}
                    </p>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-4 flex justify-between items-center">
                    <p className="font-semibold text-slate-900 dark:text-white">Total Liabilities</p>
                    <p className="text-xl font-bold text-rose-600 dark:text-rose-400">
                      {formatCurrency((balanceSheet?.cloud_liabilities?.committed_spend || 0) + (balanceSheet?.cloud_liabilities?.pending_invoices || 0))}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Key Insights */}
      {boardDeckData?.key_insights && (
        <Card data-testid="key-insights">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Key Insights for the Board</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {boardDeckData.key_insights.map((insight, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <ChevronRight className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-slate-700 dark:text-slate-300">{insight}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CloudPL;
